import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import type { MetricItem } from "../../Common/types";
import { status as statusKeys, WellnessScoreStatus } from "../../Common/types";
import { getWellbeingStatus } from "../InfoCard/utils";
import {
  parseHealthProfileDataAsNumber,
  parseHealthProfileDataAsString,
} from "@/features/HealthScore/utils/parseHealthProfileData";

export type MentalWellbeingMetric = MetricItem & {
  score: number;
};

function getItemByType(
  items: HealthProfileSuccessResponseData["items"],
  questionType: string
) {
  return items.find((i) => i.questionType === questionType);
}

function wellnessStatusToKey(status: WellnessScoreStatus): string {
  if (status === WellnessScoreStatus.Healthy) return statusKeys.healthy;
  if (status === WellnessScoreStatus.NeedsAttention)
    return statusKeys.needAttention;
  return statusKeys.needsSupport;
}

/** GAD-2: UncontrollableWorry + NervousOrOnEdge. 0 GREEN, 1-2 AMBER, >=3 RED */
/** PHQ-2: FeelingDownOrHopeless + LowInterestOrPleasure. Same scoring */
export function getMentalWellbeingMetricsFromHealthProfile(
  healthProfile: HealthProfileSuccessResponseData | null | undefined,
  t: (key: string) => string
): {
  anxiety: MentalWellbeingMetric;
  depression: MentalWellbeingMetric;
} | null {
  if (!healthProfile?.items?.length) return null;

  const items = healthProfile.items;

  const depressionScoreItem = getItemByType(items, "Phq2Score");
  const depressionScore =
    parseHealthProfileDataAsNumber(depressionScoreItem?.data?.value) ?? 0;

  const anxietyScoreItem = getItemByType(items, "Gad2Score");
  const anxietyScore =
    parseHealthProfileDataAsNumber(anxietyScoreItem?.data?.value) ?? 0;

  if (!anxietyScoreItem?.data?.value && !depressionScoreItem?.data?.value) {
    return null;
  }

  const anxietyWellnessStatus = getWellbeingStatus(anxietyScore);
  const depressionWellnessStatus = getWellbeingStatus(depressionScore);
  const anxietyStatusKey = wellnessStatusToKey(anxietyWellnessStatus);
  const depressionStatusKey = wellnessStatusToKey(depressionWellnessStatus);

  const riskLabelKey = (status: WellnessScoreStatus) => {
    if (status === WellnessScoreStatus.Healthy)
      return "MentalWellbeing.Category.riskLabel.lower";
    if (status === WellnessScoreStatus.NeedsAttention)
      return "MentalWellbeing.Category.riskLabel.higher";
    return "MentalWellbeing.Category.riskLabel.high";
  };

  return {
    anxiety: {
      id: "anxiety",
      title: t("MentalWellbeing.Category.metrics.anxiety"),
      value: t(riskLabelKey(anxietyWellnessStatus)),
      status: anxietyStatusKey,
      showChevron: true,
      score: anxietyScore,
    },
    depression: {
      id: "depression",
      title: t("MentalWellbeing.Category.metrics.depression"),
      value: t(riskLabelKey(depressionWellnessStatus)),
      status: depressionStatusKey,
      showChevron: true,
      score: depressionScore,
    },
  };
}
